import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPromoCode, deletePromoCode, listPromoCodes, updatePromoCode } from '../../../services/adminApi';
import useConfirm from '../shell/useConfirm';
import { Switch, Tag } from './ui';
import './catalog.css';

// Codes promo GLOBAUX : un code vaut pour tout panier (livre imprime, PDF, pack), retire un pourcentage du prix du ou des produits,
// jamais de la livraison. La remise est figee sur la commande a sa creation ; l'utilisation = commandes PAYEES portant le code.

// Sans caracteres ambigus (0/O, 1/I) : un code lu au telephone ou recopie ne se trompe pas.
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const genererCode = (longueur = 8, hasard = Math.random) => Array.from({ length: longueur }, () => ALPHABET[Math.floor(hasard() * ALPHABET.length)]).join('');

/** Etat lisible d'un code a l'instant `maintenant`. */
export function etatCode(c, maintenant = Date.now()) {
  if (!c.active) return 'inactive';
  if (c.startsAt && new Date(c.startsAt).getTime() > maintenant) return 'notStarted';
  if (c.expiresAt && new Date(c.expiresAt).getTime() <= maintenant) return 'expired';
  if (c.maxUses && c.uses >= c.maxUses) return 'exhausted';
  return 'active';
}
const TON = { active: 'ok', inactive: 'mute', expired: 'warn', notStarted: 'warn', exhausted: 'warn' };

const FORMULAIRE_VIDE = { code: '', percentOff: '10', expiresAt: '', maxUses: '', note: '' };

export default function PromoSection() {
  const { t, i18n } = useTranslation('admin');
  const [codes, setCodes] = useState(null);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const [f, setF] = useState(FORMULAIRE_VIDE);
  const [enCours, setEnCours] = useState(false);
  const [confirmer, confirmation] = useConfirm();

  const charger = useCallback(async () => {
    try { setCodes((await listPromoCodes()).codes); setErreur(''); } catch (err) { setErreur(err?.message || t('catalog.promo.loadFailed')); }
  }, [t]);
  useEffect(() => { charger(); }, [charger]);

  const maj = (champ, valeur) => setF((prec) => ({ ...prec, [champ]: valeur }));

  const creer = async (evenement) => {
    evenement.preventDefault();
    setErreur('');
    setMessage('');
    setEnCours(true);
    try {
      const corps = {
        code: f.code,
        percentOff: Number(f.percentOff),
        // « fin de journee » : un code valable « jusqu'au 31 » l'est toute la journee du 31.
        expiresAt: f.expiresAt ? new Date(`${f.expiresAt}T23:59:59`).toISOString() : null,
        maxUses: f.maxUses === '' ? null : Number(f.maxUses),
        note: f.note
      };
      const cree = await createPromoCode(corps);
      setMessage(t('catalog.promo.created', { code: cree.code.code }));
      setF(FORMULAIRE_VIDE);
      setOuvert(false);
      await charger();
    } catch (err) {
      setErreur(err?.message || t('catalog.promo.saveFailed'));
    } finally {
      setEnCours(false);
    }
  };

  const basculer = async (c, actif) => {
    setErreur('');
    setMessage('');
    if (!actif && !(await confirmer({
      title: t('catalog.promo.deactivateTitle', { code: c.code }),
      message: t('catalog.promo.deactivateBody'),
      confirmLabel: t('catalog.promo.deactivateYes')
    }))) return;
    try {
      await updatePromoCode(c.code, { active: actif });
      setMessage(t('catalog.promo.updated', { code: c.code }));
      await charger();
    } catch (err) {
      setErreur(err?.message || t('catalog.promo.saveFailed'));
    }
  };

  const supprimer = async (c) => {
    setErreur('');
    setMessage('');
    if (!(await confirmer({
      title: t('catalog.promo.deleteTitle', { code: c.code }),
      message: t('catalog.promo.deleteBody'),
      confirmLabel: t('catalog.promo.deleteYes')
    }))) return;
    try {
      await deletePromoCode(c.code);
      setMessage(t('catalog.promo.deleted', { code: c.code }));
      await charger();
    } catch (err) {
      setErreur(err?.message || t('catalog.promo.saveFailed'));
    }
  };

  return (
    <section className="adm-card" aria-labelledby="adm-promo-titre">
      <div className="adm-toolbar">
        <h2 id="adm-promo-titre">{t('catalog.promo.title')}</h2>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => { setOuvert((v) => !v); setMessage(''); }} aria-expanded={ouvert}>{t('catalog.promo.create')}</button>
      </div>
      <p className="adm-note">{t('catalog.promo.note')}</p>
      {confirmation}
      {message && <p className="adm-note adm-flash" role="status">{message}</p>}
      {erreur && <p className="adm-error" role="alert">{erreur}</p>}

      {ouvert && (
        <form className="adm-form adm-promo-form" onSubmit={creer}>
          <label className="adm-field">
            <span>{t('catalog.promo.form.code')}</span>
            <span className="adm-inline">
              <input className="adm-input" value={f.code} maxLength={24} autoComplete="off" onChange={(e) => maj('code', e.target.value)} required />
              <button type="button" className="btn btn-outline btn-sm" onClick={() => maj('code', genererCode())}>{t('catalog.promo.generate')}</button>
            </span>
            <small>{t('catalog.promo.form.codeHelp')}</small>
          </label>
          <label className="adm-field">
            <span>{t('catalog.promo.form.percent')}</span>
            <input className="adm-input" type="number" min="1" max="90" value={f.percentOff} onChange={(e) => maj('percentOff', e.target.value)} required />
            <small>{t('catalog.promo.form.percentHelp')}</small>
          </label>
          <label className="adm-field">
            <span>{t('catalog.promo.form.expires')}</span>
            <input className="adm-input" type="date" value={f.expiresAt} onChange={(e) => maj('expiresAt', e.target.value)} />
            <small>{t('catalog.promo.form.expiresHelp')}</small>
          </label>
          <label className="adm-field">
            <span>{t('catalog.promo.form.maxUses')}</span>
            <input className="adm-input" type="number" min="1" value={f.maxUses} onChange={(e) => maj('maxUses', e.target.value)} />
            <small>{t('catalog.promo.form.maxUsesHelp')}</small>
          </label>
          <label className="adm-field is-wide">
            <span>{t('catalog.promo.form.note')}</span>
            <input className="adm-input" value={f.note} maxLength={200} onChange={(e) => maj('note', e.target.value)} />
          </label>
          <div className="adm-actions is-wide">
            <button type="submit" className="btn btn-primary btn-sm" disabled={enCours || !f.code.trim()}>{enCours ? t('catalog.promo.form.submitting') : t('catalog.promo.form.submit')}</button>
          </div>
        </form>
      )}

      {codes === null && !erreur && <p className="adm-state" role="status">{t('catalog.common.loading')}</p>}
      {codes && codes.length === 0 && <p className="adm-note">{t('catalog.promo.empty')}</p>}
      {codes && codes.length > 0 && (
        <div className="adm-tbl">
          <table>
            <thead>
              <tr>
                <th>{t('catalog.promo.cols.code')}</th><th className="num">{t('catalog.promo.cols.percent')}</th><th>{t('catalog.promo.cols.status')}</th>
                <th className="num">{t('catalog.promo.cols.uses')}</th><th>{t('catalog.promo.cols.until')}</th><th>{t('catalog.promo.cols.note')}</th><th />
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => {
                const etat = etatCode(c);
                return (
                  <tr key={c.code}>
                    <td><b>{c.code}</b></td>
                    <td className="num">−{c.percentOff} %</td>
                    <td><Tag tone={TON[etat]}>{t(`catalog.promo.states.${etat}`)}</Tag></td>
                    <td className="num">{c.uses} / {c.maxUses || t('catalog.promo.unlimited')}</td>
                    <td>{c.expiresAt ? new Date(c.expiresAt).toLocaleDateString(i18n.language) : t('catalog.promo.noEnd')}</td>
                    <td>{c.note || '—'}</td>
                    <td className="adm-promo-actions">
                      <Switch checked={c.active} onChange={(v) => basculer(c, v)} label={t('catalog.promo.switchLabel', { code: c.code })} />
                      {c.uses === 0 && <button type="button" className="btn btn-outline btn-sm" onClick={() => supprimer(c)}>{t('catalog.promo.delete')}</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
